// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { Modal } from '@ta/overlays.modal';
import { TitleBar } from '@ta/modal.headers';
import { TextArea } from '@ta/input.text-area';
import { localize } from '@ta/common.i18n';

import { SubmitIAPVoteButton } from './submit/SubmitIapVote';
import { ReportVoteThankYou, ReportVoteError } from './ContentModals';
import styles from './ReportOwnerResponseModal.less';

const ContentType = Object.freeze({
  USERREVIEW: 'USERREVIEW',
});

const IAPReason = Object.freeze({
  OWNER_RESPONSE_REASON_ID: 21,
});

type ExternalProps = {|
  userId: ?string,
  modalName: string,
  responseId: number,
|};

type Props = {|
  ...ExternalProps,
  csrfToken: string,
  locationId: ?number,
|};

type State = {|
  status: 'panel' | 'thankyou' | 'error',
  comment: string,
|};

/**
 * Modal for reporting an owner's response to a review by a member.
 */
class ReportOwnerResponseModal extends React.Component<Props, State> {
  state = {
    status: 'panel',
    comment: '',
  };

  onError = () => {
    this.setState({ status: 'error' });
  };

  onComplete = () => {
    this.setState({ status: 'thankyou' });
  };

  getSubmissionState = () => {
    // comment less than 10 chars return
    if (this.state.comment.length < 10) {
      return false;
    }

    return {
      iapReason: IAPReason.OWNER_RESPONSE_REASON_ID,
      comment: this.state.comment,
    };
  };

  setCommentFromCommentBoxEvent = (event: any) => {
    this.setState({
      comment: event.target.value,
    });
  };

  header = (
    <TitleBar iconName="flag-fill" displayType="LEFT_ALIGN_GRAY">
      {localize('report_problem_ffffff85')}
    </TitleBar>
  );

  numberLabels = (
    <div className={styles.numberLabels}>
      {localize('ur_rd_0characterminimum', { '0': 10 })}
      &nbsp;/&nbsp;
      {localize('ur_rd_0charactermaximum', { '0': 200 })}
    </div>
  );

  createSubmitButton = () => (
    <SubmitIAPVoteButton
      contentId={this.props.responseId}
      contentType={ContentType.USERREVIEW}
      userId={this.props.userId}
      csrfToken={this.props.csrfToken}
      submissionState={this.getSubmissionState()}
      showBackButton={false}
      onComplete={this.onComplete}
      onError={this.onError}
    />
  );

  render() {
    const { modalName, userId, locationId, responseId } = this.props;

    if (!modalName || !userId || !responseId || !locationId || locationId <= 0) {
      return null;
    }

    let content;
    if (this.state.status === 'thankyou') {
      content = <ReportVoteThankYou />;
    } else if (this.state.status === 'error') {
      content = <ReportVoteError />;
    } else {
      content = (
        <div className={styles.modalContainer}>
          <div className={styles.header}>
            {localize(
              'ric_v2_owner_resp_violates_guidelines_ffffec52',
              /* Please explain why you think this management response is inappropriate and does not meet */
            )}
            <a
              className={styles.guidelineLink}
              href="/help/management_response_guidelines"
              target="_blank"
            >
              {localize('ric_v2_guidelines_ffffd9be' /* guidelines. */)}
            </a>
          </div>
          <>
            <TextArea
              className={styles.commentBox}
              value={this.state.comment}
              onChange={this.setCommentFromCommentBoxEvent}
              maxLength={200}
            />
            {this.numberLabels}
          </>
          {this.createSubmitButton()}
        </div>
      );
    }

    return (
      <Modal name={modalName} header={this.header}>
        {content}
      </Modal>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  csrfToken: state.auth.csrfToken,
  locationId: state.page.detailId,
}))(ReportOwnerResponseModal);
